import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllDetails, postAllData, postSingleData } from "../../service/ProductService";
import { CHANGE_DEFAULT_PAYMENT_CARD, GET_PROFILE_DATA_BY_ID, UPDATE_CLIENT_DATA } from "../../config/ApiConstant";
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { RadioButton } from "primereact/radiobutton";
import { SkeletonbodyTemplate, Skeletonitems } from "../../appconfig/Settings";
import { Checkbox } from "primereact/checkbox";
import { Badge } from "primereact/badge";
import moment from "moment";
import { validateClientChargePaymentBy } from "../../config/Validate";
import { Dialog } from "primereact/dialog";
import UpdatePaymentMethod from "../../components/BankDetails/BankDetails";
import Loader from "../../components/Loader/Loader";

const UserProfile = () => {
  // get userData from local storage
  const rawUserData = localStorage.getItem("user_data");
  let userData: any = null;
  userData = rawUserData ? JSON.parse(rawUserData) : null;
  const userRole = userData?.roles?.role_name || "";

  //   navigate
  const navigate = useNavigate();

  //   toast ref
  const toast = useRef<any>(null);

  // error state
  const [errors, setErrors] = useState<any>({});

  // loading
  const [pageLoading, setPageLoading] = useState<any>(true);

  // btn submit loading
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  // profile data obj
  const [profileData, setProfileData] = useState<any>();

  // profile details using api
  const [profileDetailsUpdate, setProfileDetailsUpdate] = useState<any>({
    client_charge_payment_by: "Self",
  });

  // stripe card detail form model
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);

  // card details
  const [cardDetailsList, setCardDetailsList] = useState<any>([]);

  // states to change default payment card
  const [showDefaultModal, setShowDefaultModal] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [cardSubmitLoading, setCardSubmitLoading] = useState<boolean>(false);

  // get profile data
  useEffect(() => {
    getProfileData();
  }, []);

  // get profile data
  const getProfileData = async () => {
    try {
      setPageLoading(true);
      const response = await getAllDetails(`${GET_PROFILE_DATA_BY_ID}/${userData?.id}`);

      if (response) {
        setCardDetailsList(response?.card_details);
        // set local storage with card detail added or not (flag)
        const payment_details_added = response?.payment_details_added;
        localStorage.setItem("payment_details_added", payment_details_added);
        setProfileData(response);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message,
      });
    } finally {
      setPageLoading(false);
    }
  };

  // on input change for client charge payment by
  const onInputHandleChange = (e: any, name: string) => {
    let val = e?.target?.value ?? e?.value;

    setProfileDetailsUpdate({ ...profileDetailsUpdate, [name]: val });
    setErrors({ ...errors, [name]: "" });
  };

  // api call to change default payment card
  const handleMakeDefault = async () => {
    try {
      setCardSubmitLoading(true);
      const response = await postSingleData(`${CHANGE_DEFAULT_PAYMENT_CARD}/${selectedCard?.id}`);
      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        setShowDefaultModal(false);
        getProfileData();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setCardSubmitLoading(false);
    }
  };

  // submit client payment for user role === User
  const submitClientPaymentData = async () => {
    try {
      // const { isError, errors } = validateClientChargePaymentBy({
      //   client_charge_payment_by: clientPayment,
      // });

      const { isError, errors } = validateClientChargePaymentBy(profileDetailsUpdate);

      setErrors(errors);
      if (isError) return;

      setSubmitLoading(true);

      // payload
      const payload = {
        paymentStatus: profileDetailsUpdate?.client_charge_payment_by,
      };

      // api call
      const response = await postAllData(UPDATE_CLIENT_DATA + "/" + profileData.id, payload);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  // card number template
  const cardNumberTemplate = (rowData: any) => {
    return <div className="">{`********${rowData?.last4}`}</div>;
  };

  // on default card change
  const onDefaultCheckboxClick = (rowData: any) => {
    if (rowData.is_default) return;

    setSelectedCard(rowData);
    setShowDefaultModal(true);
  };

  // default selected card template
  const defaultSelectedCard = (rowData: any) => {
    return (
      <>
        <Checkbox checked={rowData.is_default} onChange={() => onDefaultCheckboxClick(rowData)} className="mr-2" />

        {rowData.is_default && <Badge value="Default" severity="success" />}
      </>
    );
  };

  // month and year expire date
  const expiryTemplate = (rowData: any) => {
    const date = moment(`${rowData?.exp_year}-${rowData?.exp_month}-01`, "YYYY-M-D");

    return <span>{date.format("MMM, YYYY")}</span>;
  };

  return (
    <>
      <AppBreadcrumb
        pageName="Profile"
        currentMenu="User"
        pageTitle={
          <>
            <div className="gotopage-button-header">
              <Button icon="pi pi-arrow-left" className="mr-2 common-p-gotopage-button" onClick={() => navigate("/user-dashboard")} />
              <span className="span-text">Profile</span>
            </div>
          </>
        }
      />
      <div className="relative">
        <Card className="p-0 bg-white">
          <Toast ref={toast} />
          <div className="grid">
            {/* name */}
            <div className="flex mb-3 col-12">
              <div className="">
                <label className="font-bold text-gray-700">Name :</label>
                <div className="flex gap-2 align-items-center">
                  <h2 className="mt-1 mb-1 text-xl font-semibold text-gray-900">
                    {profileData?.first_name ?? ""} {profileData?.last_name ?? ""}
                  </h2>
                </div>
              </div>
            </div>
            {/* other details */}
            <div className="col-12 md:col-12">
              <div className="grid p-fluid">
                {/* email */}
                {!window.cn(profileData?.email) && (
                  <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                    <label className="font-bold text-gray-700">Email:</label>
                    <div className="text-gray-900">{profileData?.email || "-"}</div>
                  </div>
                )}

                {/* phone */}
                {!window.cn(profileData?.phone) && (
                  <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                    <label className="font-bold text-gray-700">Phone:</label>
                    <div className="text-gray-900">{profileData?.phone || "-"}</div>
                  </div>
                )}

                {/* Address */}
                {!window.cn(profileData?.address) && (
                  <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                    <label className="font-bold text-gray-700">Address:</label>
                    <div className="text-gray-900">{userData?.address || "-"}</div>
                  </div>
                )}

                {/* postal code */}
                {!window.cn(profileData?.postal_code) && (
                  <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                    <label className="font-bold text-gray-700">Postal Code:</label>
                    <div className="text-gray-900">{profileData?.postal_code || "-"}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* card details table */}
        <div className="grid mt-5">
          <div className="col-12 md:col-8">
            <Card className=" bg-white payment-detail-table ">
              <div className="card-header mb-3 flex justify-content-between">
                <h5 className="font-bold text-gray-700 mb-0">Card Details</h5>
                <Button label="Add Card" icon="pi pi-plus" className="common-primary-button" onClick={() => setShowPaymentModal(true)} />
              </div>
              {!pageLoading ? (
                <DataTable
                  className="p-datatable-customers"
                  value={cardDetailsList}
                  responsiveLayout={"scroll"}
                  dataKey="id"
                  emptyMessage="No Card Details Found"
                  rowHover
                >
                  <Column field="brand" header="Brand" style={{ minWidth: "14rem" }}></Column>
                  <Column field="last4" header="Card No" style={{ minWidth: "10rem" }} body={cardNumberTemplate}></Column>

                  <Column header="Expiry" body={expiryTemplate} style={{ minWidth: "10rem" }} />
                  <Column field="is_default" header="Default Card" body={defaultSelectedCard}></Column>
                </DataTable>
              ) : (
                <DataTable value={Skeletonitems}>
                  <Column field="brand" header="Brand" style={{ minWidth: "14rem" }} body={SkeletonbodyTemplate} />
                  <Column field="last4" header="Card No" style={{ minWidth: "10rem" }} body={SkeletonbodyTemplate} />
                  <Column header="Expiry" style={{ minWidth: "10rem" }} body={SkeletonbodyTemplate} />
                  <Column field="is_default" header="Default Card" body={SkeletonbodyTemplate} />
                </DataTable>
              )}
            </Card>
          </div>

          {/* client payment card */}
          <div className="col-12 md:col-4 ">
            <Card
              className="p-0 bg-white"
              footer={
                <>
                  <div className="flex pt-2 align-items-center justify-content-end p-0">
                    <Button label="Submit" icon="pi pi-check" className="common-primary-button" disabled={true} onClick={submitClientPaymentData} />
                  </div>
                </>
              }
            >
              <div className="card-header">
                <h5 className="font-bold text-gray-700 mb-3">Client Payment Paid By</h5>
                <div className="">
                  {/* client charge payment by */}
                  <div className="field col-12 mb-0 ">
                    <div className="flex gap-4 ">
                      <div className="flex align-items-center">
                        <RadioButton
                          inputId="client_charge_payment_by"
                          name="client_charge_payment_by"
                          value="Self"
                          onChange={(e) => onInputHandleChange(e, "client_charge_payment_by")}
                          checked={profileDetailsUpdate?.client_charge_payment_by === "Self"}
                        />
                        <label htmlFor="Self" className="ml-2">
                          Self
                        </label>
                      </div>
                      <div className="flex align-items-center">
                        <RadioButton
                          inputId="client_charge_payment_by"
                          name="client_charge_payment_by"
                          value="Client"
                          disabled={true}
                          // onChange={(e: any) => setClientPayment(e.value)}
                          // checked={clientPayment === "Client"}
                          onChange={(e) => onInputHandleChange(e, "client_charge_payment_by")}
                          checked={profileDetailsUpdate?.client_charge_payment_by === "Client"}
                        />
                        <label htmlFor="Client" className="ml-2">
                          Client
                        </label>
                      </div>
                    </div>
                    <small className="p-invalid-txt">{errors["client_charge_payment_by"]}</small>
                  </div>
                </div>
              </div>
              <div></div>
            </Card>
          </div>
        </div>

        {/* page loader */}
        {pageLoading === true && <Loader />}
      </div>

      {/* dialog for stripe card detail form */}
      <Dialog
        header="Add Card Details"
        visible={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        modal
        draggable={false}
        dismissableMask={false}
        style={{ width: "35rem" }}
        className="mx-3 lg:mx-0"
        footer={<div></div>}
      >
        <UpdatePaymentMethod customerId={userData?.customer_id} onSuccess={() => setShowPaymentModal(false)} getApiCall={() => getProfileData()} />
      </Dialog>

      {/* dialog to change default payment card */}
      <Dialog
        header="Make Default Card"
        visible={showDefaultModal}
        style={{ width: "400px" }}
        className="mx-3 lg:mx-0"
        draggable={false}
        onHide={() => setShowDefaultModal(false)}
        footer={
          <>
            <div className="flex justify-content-end gap-2">
              <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={() => setShowDefaultModal(false)} />
              <Button
                label="Confirm"
                icon="pi pi-check"
                loading={cardSubmitLoading}
                className="common-primary-button"
                onClick={() => handleMakeDefault()}
              />
            </div>
          </>
        }
      >
        <h6>Are you sure you want to make this card as your default payment card?</h6>
      </Dialog>
    </>
  );
};

export default UserProfile;
